import safeDispatch from "data/util/safeDispatch";
import OverActions from "data/OverActions";
import Dispatcher from "../Dispatcher";

/**
 * I am a factory function for creating overs. I am passed over options, and
 * return a function that can be used to "wrap" a work callback with managed
 * over according to the passed options.
 *
 * <p>The returned function should be invoked with a callback. When invoked, an
 * over will be shown, then the callback itself invoked, then the over hidden.
 * If the callback returns a thenable, the over will be hidden asynchronously.
 * All other return types will cause the over to be hidden synchronously.
 *
 * <p>It is deliberate that a callback is passed and not a thenable; this lets
 * the over render synchonously, before the callback is invoked to actually
 * initiate the work beneath the over. Otherwise any setup (e.g., the body of
 * the function passed to the Promise constructor) will run before the over is
 * visible.
 *
 * <p>For simple uses, the curried form is klunky, so it may be invoked
 * uncurried for the same effect. Semantics are exactly the same, except the
 * function produced by the factory is hidden. For even simpler uses where no
 * options are needed, the work callback may be passed as the sole argument.
 *
 * <p>Below are examples of all invocation styles:
 *
 * <pre>
 * // full curried
 * const one1 = withOver({message: "..."})(() => { ...; return 1; });
 * // full uncurried
 * const one2 = withOver({message: "..."}, ( => { ...; return 1; });
 * // no options curried
 * const one3 = withOver()(() => { ...; return 1; });
 * // no options uncurried
 * const one4 = withOver(() => { ...; return 1; });
 * </pre>
 *
 * @param options the options to use for all overs created by the returned
 *  function
 * @param uncurriedWork OPTIONAL work to be invoked directly in an uncurried
 *  fashion.
 * @returns {Function} a function accepting work that should have an over
 *  displayed for it's duration.
 */
function withOver(options = {}, uncurriedWork) {
    let invokeImmediate = arguments.length >= 2;
    if (arguments.length === 1 && typeof options === "function") {
        uncurriedWork = options;
        options = {};
        invokeImmediate = true;
    }
    if (typeof options === "string") {
        options = {
            message: options,
        };
    }
    const retfunc = work => {
        const overKey = `with-over-${Date.now().toString(36)}-${Math.random().toString(36).substr(2)}`;
        Dispatcher.dispatch({
            type: OverActions.SHOW,
            options: {...options, key: overKey}
        });
        const doHide = v => {
            Dispatcher.dispatch({
                type: OverActions.HIDE,
                key: overKey
            });
            return v;
        };
        let hideAfter = true;
        try {
            const result = work.call(null);
            if (result != null && typeof result.then === "function") {
                Promise.resolve(result).finally(doHide);
                hideAfter = false;
            }
            return result;
        } finally {
            if (hideAfter) {
                doHide();
            }
        }
    };
    return invokeImmediate ? retfunc(uncurriedWork) : retfunc;
}

// i don't really belong here. per usual, options may be a message string, but
// only message and title keys are accepted
export const appAlert = (options, onOk) => {
    if (typeof options === "string" || typeof options === "function") {
        options = { message: options };
    }
    safeDispatch({
        type: OverActions.APP_ALERT,
        message: options.message,
        actions: options.actions,
        title: options.title,
        callback: onOk,
    });
};

export default withOver;



// WEBPACK FOOTER //
// ./app/lib/withOver.js